import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, Grid, FormControl,
  InputLabel, Select, MenuItem, IconButton, Divider,
  CircularProgress, makeStyles, Chip
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import DeleteIcon from '@material-ui/icons/Delete';
import { useDispatch, useSelector } from 'react-redux';
import { uploadProductImages, deleteProductImage } from 'src/features/productSlice';
import { showGlobalToast } from 'src/features/uiSlice';
import { messageToastType_const } from 'src/app/constant/config';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const cleanServer = (process.env.REACT_APP_IMG_SERVER || '').replace(/\/+$/, '');
  const cleanUrl = url.replace(/^\/+/, '');
  return `${cleanServer}/${cleanUrl}`;
};

const useStyles = makeStyles(() => ({
  dialogTitle: {
    background: 'linear-gradient(135deg, #D9042F 0%, #a80020 100%)',
    color: '#fff',
    padding: '16px 24px'
  },
  titleText: {
    fontWeight: 700,
    fontSize: 18,
    color: '#fff'
  },
  closeBtn: {
    color: '#fff'
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: 13,
    color: '#D9042F',
    marginBottom: 8,
    marginTop: 16
  },
  imageGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 8
  },
  imageThumb: {
    position: 'relative',
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    border: '2px solid #f5c2c7',
    cursor: 'pointer',
    '&:hover $deleteOverlay': { opacity: 1 }
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  deleteOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.2s'
  },
  addImageBtn: {
    width: 80,
    height: 80,
    border: '2px dashed #f5c2c7',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#D9042F',
    '&:hover': { backgroundColor: '#fff0f3', borderColor: '#D9042F' },
    transition: 'all 0.15s'
  },
  saveBtn: {
    backgroundColor: '#D9042F !important',
    color: '#fff !important',
    fontWeight: 700,
    borderRadius: 8,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#b30222 !important'
    },
    '&.Mui-disabled': {
      backgroundColor: '#f5f5f5 !important',
      color: '#c0c0c0 !important'
    }
  },
  dialogContent: {
    '& input::placeholder': {
      fontSize: '12px !important'
    },
    '& textarea::placeholder': {
      fontSize: '12px !important'
    }
  }
}));

const EMPTY_FORM = {
  code: '',
  name: '',
  unit: '',
  includes: '',
  description: '',
  category: '',
  version: '',
  init_agency_id: '',
  init_quantity: ''
};

const ProductDialog = ({
  open,
  onClose,
  editingProduct,
  agencies = [],
  defaultAgencyId = '',
  onSave,
  dispatch: parentDispatch
}) => {
  const classes = useStyles();
  const dispatch = useDispatch() || parentDispatch;
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [existingImages, setExistingImages] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(editingProduct);

  useEffect(() => {
    if (open) {
      if (editingProduct) {
        setForm({
          code: editingProduct.code || '',
          name: editingProduct.name || '',
          unit: editingProduct.unit || '',
          includes: editingProduct.includes || '',
          description: editingProduct.description || '',
          category: editingProduct.category || '',
          version: editingProduct.version || '',
          init_agency_id: '',
          init_quantity: ''
        });
        setExistingImages(editingProduct.images || []);
      } else {
        setForm({ ...EMPTY_FORM, init_agency_id: defaultAgencyId });
        setExistingImages([]);
      }
      setNewImageFiles([]);
      setNewImagePreviews([]);
    }
  }, [open, editingProduct, defaultAgencyId]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImagePick = e => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const previews = files.map(f => URL.createObjectURL(f));
    setNewImageFiles(prev => [...prev, ...files]);
    setNewImagePreviews(prev => [...prev, ...previews]);
    e.target.value = '';
  };

  const handleRemoveNewImage = idx => {
    URL.revokeObjectURL(newImagePreviews[idx]);
    setNewImageFiles(prev => prev.filter((_, i) => i !== idx));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteExistingImage = async (img) => {
    await dispatch(deleteProductImage({ id: img.id }));
    setExistingImages(prev => prev.filter(i => i.id !== img.id));
  };

  const validate = () => {
    if (!form.name.trim()) {
      dispatch(showGlobalToast({ message: 'Vui lòng nhập tên sản phẩm!', type: messageToastType_const.warning }));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    const payload = {
      name: form.name.trim(),
      unit: form.unit.trim() || undefined,
      includes: form.includes.trim() || undefined,
      description: form.description.trim() || undefined,
      category: form.category.trim() || undefined,
      version: form.version.trim() || undefined
    };

    if (!isEdit) {
      if (form.code.trim()) payload.code = form.code.trim();
      if (form.init_quantity) {
        payload.init_quantity = Number(form.init_quantity);
        const vmakeAgency = (agencies || []).find(a => a.name?.toLowerCase()?.includes('vmake')) || { id: 1 };
        payload.init_agency_id = vmakeAgency.id;
      }
    }

    try {
      // Save product (create/update) and upload images
      await onSave(payload, isEdit, newImageFiles);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.titleText}>
            {isEdit ? `Sửa sản phẩm: ${editingProduct?.code || editingProduct?.name}` : 'Thêm sản phẩm mới'}
          </Typography>
          <IconButton size="small" className={classes.closeBtn} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        <Box pt={1}>
          {/* Basic Info */}
          <Typography className={classes.sectionLabel}>Thông tin cơ bản</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InputLabel style={{ fontSize: 13, color: '#1a1f36', fontWeight: 600, marginBottom: 4 }}>
                Mã sản phẩm
              </InputLabel>
              <TextField
                className="input-no-fieldset"
                name="code"
                value={form.code}
                onChange={handleChange}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Tự sinh nếu để trống"
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel style={{ fontSize: 13, color: '#1a1f36', fontWeight: 600, marginBottom: 4 }}>
                Đơn vị tính
              </InputLabel>
              <TextField
                className="input-no-fieldset"
                name="unit"
                value={form.unit}
                onChange={handleChange}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Cái, Bộ, Chiếc..."
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel style={{ fontSize: 13, color: '#1a1f36', fontWeight: 600, marginBottom: 4 }}>
                Tên sản phẩm <span style={{ color: 'red' }}>*</span>
              </InputLabel>
              <TextField
                className="input-no-fieldset"
                name="name"
                value={form.name}
                onChange={handleChange}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Nhập tên sản phẩm"
                required
              />
            </Grid>


            <Grid item xs={12}>
              <InputLabel style={{ fontSize: 13, color: '#1a1f36', fontWeight: 600, marginBottom: 4 }}>
                Bao gồm / kèm theo
              </InputLabel>
              <TextField
                className="input-no-fieldset"
                name="includes"
                value={form.includes}
                onChange={handleChange}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="VD: 1 dây nguồn, 1 bracket..."
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel style={{ fontSize: 13, color: '#1a1f36', fontWeight: 600, marginBottom: 4 }}>
                Mô tả
              </InputLabel>
              <TextField
                className="input-no-fieldset"
                name="description"
                value={form.description}
                onChange={handleChange}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={3}
                placeholder="Nhập mô tả sản phẩm"
              />
            </Grid>
          </Grid>

          {/* Init stock (create only) */}
          {!isEdit && (
            <>
              <Divider style={{ margin: '20px 0 8px' }} />
              <Typography className={classes.sectionLabel}>Nhập tồn kho ban đầu (tuỳ chọn)</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InputLabel style={{ fontSize: 13, color: '#1a1f36', fontWeight: 600, marginBottom: 4 }}>
                    Số lượng ban đầu
                  </InputLabel>
                  <TextField
                    className="input-no-fieldset"
                    name="init_quantity"
                    value={form.init_quantity}
                    onChange={handleChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="number"
                    placeholder="Nhập số lượng ban đầu"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* Images */}
          <Divider style={{ margin: '20px 0 8px' }} />
          <Typography className={classes.sectionLabel}>Hình ảnh sản phẩm</Typography>
          <Box className={classes.imageGrid}>
            {existingImages.map(img => (
              <Box key={img.id} className={classes.imageThumb}>
                <img src={getImageUrl(img.url)} alt="" className={classes.thumbImg} />
                <Box className={classes.deleteOverlay}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteExistingImage(img)}
                    style={{ color: '#fff' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            {newImagePreviews.map((preview, idx) => (
              <Box key={`new-${idx}`} className={classes.imageThumb}>
                <img src={preview} alt="" className={classes.thumbImg} />
                <Box className={classes.deleteOverlay}>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveNewImage(idx)}
                    style={{ color: '#fff' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Chip
                  label="Mới"
                  size="small"
                  style={{
                    position: 'absolute', top: 2, right: 2, fontSize: 9,
                    height: 16, backgroundColor: '#27ae60', color: '#fff'
                  }}
                />
              </Box>
            ))}
            <Box className={classes.addImageBtn} onClick={() => fileInputRef.current?.click()}>
              <AddPhotoAlternateIcon fontSize="small" />
              <Typography style={{ fontSize: 10, marginTop: 2 }}>Thêm ảnh</Typography>
            </Box>
          </Box>
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleImagePick}
          />
          {!isEdit && newImageFiles.length > 0 && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
              * Ảnh sẽ được upload sau khi tạo sản phẩm thành công
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
        <Button onClick={onClose} variant="outlined" style={{ borderRadius: 8, textTransform: 'none', fontWeight: 600 }}>
          Hủy
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          className={classes.saveBtn}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : null}
          style={{ height: '36px' }}
        >
          {saving ? 'Đang lưu...' : isEdit ? 'Cập nhật' : 'Tạo sản phẩm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductDialog;
